import { Injectable } from '@angular/core';
import { SearchBasketsService } from '@epicuro-next/data/baskets';
import { switchBasketSuccess } from '@epicuro-next/state/basket-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { BasketSearchResult } from 'graphql-api/baskets-api-interfaces';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mapTo,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as switchActions from '../actions/basket-switch.actions';
import {
  selectBasketsCount as selectBasketsAmount,
  selectPage,
  selectUsersAmount,
} from '../selectors/basket-switch.selectors';

@Injectable()
export class BasketSwitchEffects {
  constructor(
    private actions$: Actions,
    private searchBasketsService: SearchBasketsService,
    private store: Store,
  ) {}

  public searchBaskets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(switchActions.searchBaskets),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(selectPage)),
            this.store.pipe(select(selectBasketsAmount)),
          ),
        ),
      ),
      switchMap(([{ refNumber }, start, basketsAmount]) =>
        this.searchBasketsService
          .searchBaskets(refNumber, start, basketsAmount)
          .pipe(
            map((data) =>
              switchActions.searchBasketsSuccess({
                data: data as BasketSearchResult,
              }),
            ),
            catchError((error) =>
              of(switchActions.searchBasketsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public searchGroupedBaskets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(switchActions.searchGroupedBaskets),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(selectPage)),
            this.store.pipe(select(selectUsersAmount)),
            this.store.pipe(select(selectBasketsAmount)),
          ),
        ),
      ),
      switchMap(([{ refNumber }, start, amountToLoad, basketsAmount]) =>
        this.searchBasketsService
          .searchUserBaskets(refNumber, start, amountToLoad, basketsAmount)
          .pipe(
            map((data) =>
              switchActions.searchGroupedBasketsSuccess({
                data,
              }),
            ),
            catchError((error) =>
              of(
                switchActions.searchGroupedBasketsFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public closeBasketManagerOnSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(switchBasketSuccess),
      mapTo(switchActions.closeBasketManager()),
    ),
  );
}
